/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2020 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import ServiceRegistry from "bundle/util/ServiceRegistry";
import getWindowStartTime from "bundle/util/TimeUtil";
import IFrameMessageTypes from "common/constants/IFrameMessageTypes";
import getFinalConfig from "app/core/EnvConfig";

const appStartTime = getWindowStartTime();

class MessageExecutor {
    execute = (type, data) => {
        if (type === IFrameMessageTypes.INIT) {
            this._initialize({ ...data.config, eventOrigin: data.eventOrigin });
        } else if (type === IFrameMessageTypes.PREVIEW) {
            this._takeAction("preview", data.fileInfo, data.previewConfig, {
                heightInView: data.heightInView,
                timeInfo: data.timeInfo,
            });
        } else if (type === IFrameMessageTypes.UPDATE_HEIGHT) {
            window.document.getElementById("adobe-dc-view").style.height = `${ data.height }px`;
        }
    };

    _initialize = config => {
        this._config = getFinalConfig(config);
        ServiceRegistry.getService("LoggingService").initialize(this._config);
        ServiceRegistry.getService("DCInitService").initialize(this._config);
        ServiceRegistry.getService("CallbackService").initialize(ServiceRegistry.getService("MessagingService"));
    };

    _takeAction = (type, fileInfoList, previewConfig, options) => {
        const fileInfos = fileInfoList.map(fileInfo => this._getFinalFileInfo(fileInfo));
        const config = { ...previewConfig || {} };
        this._executeAction(type, fileInfos, config, options);
    };

    _getFinalFileInfo = fileInfo => {
        const newFileInfo = {
            metaData: fileInfo.metaData,
        };
        if (fileInfo.rendition) {
            const rendition = {};
            Object.keys(fileInfo.rendition).forEach(pageIndex => {
                rendition[pageIndex] = this._processContent(fileInfo.rendition[pageIndex]);
            });
            newFileInfo.rendition = rendition;
        }
        if (fileInfo.content) {
            newFileInfo.content = this._processContent(fileInfo.content);
        }
        return newFileInfo;
    };

    _processContent = content => {
        const newContent = {};
        const promiseId = content.promise;
        const infoPromiseId = content.infoPromise;
        const initialBufferPromiseId = content.initialBufferPromise;
        if (promiseId) {
            newContent.promise = ServiceRegistry.getService("MessagingService").receivePromise(promiseId);
        }
        if (infoPromiseId) {
            newContent.infoPromise = ServiceRegistry.getService("MessagingService")
                .receivePromise(infoPromiseId);
        }
        if (initialBufferPromiseId) {
            newContent.initialBufferPromise = ServiceRegistry.getService("MessagingService")
                .receivePromise(initialBufferPromiseId);
        }
        if (content.location) {
            newContent.location = content.location;
        }
        return newContent;
    };

    _executeAction = (action, fileInfoList, actionConfig, options) => {
        if (window && window.renderApp) {
            ServiceRegistry.getService("LoggingService").logExternal("Executing action", action);
            const data = {
                action,
                config: this._config,
                services: ServiceRegistry.getServices(),
                fileInfoList,
                actionConfig,
                heightInView: options.heightInView,
                timeInfo: { ...options.timeInfo, appStartTime },

            };
            window.renderApp(data);
        } else {
            setTimeout(() => {
                this._executeAction(action, fileInfoList, actionConfig, options);
            }, 5);
        }
    };
}

const messageExecutor = new MessageExecutor();
export default messageExecutor;
